import type { MantineNumberSize } from '@mantine/core';
import { Box, useMantineTheme } from '@mantine/core';
import * as Plot from '@observablehq/plot';
import type { QualityScoreHistoryPoint } from '@repo/api-codegen';
import dayjs from 'dayjs';
import { svg } from 'htl';
import { isNil, uniq } from 'lodash-es';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pointer } from '../../../utils/charts/TooltipMark/pointer';
import {
	cleanUpTooltips,
	tooltip,
} from '../../../utils/charts/TooltipMark/tooltipMark';
import {
	PolicyTimeseriesTooltipV2,
	type PolicyTimeseriesTooltipPolicyV2,
} from './PolicyTimeseriesTooltipV2';

const CHART_CONFIG = {
	MARGIN_TOP: 10,
	MARGIN_BOTTOM: 30,
	MARGIN_LEFT: 50,
	MARGIN_RIGHT: 20,
	Y_DOMAIN: [0, 100] as const,
	GRID_TICKS: [0, 25, 50, 75, 100] as const,
	AXIS_FONT_SIZE: 12,
	TICK_PADDING: 16,
} as const;

interface ChartPoint {
	date: Date;
	isoDate: string;
	percentage: number;
	policies: PolicyTimeseriesTooltipPolicyV2[];
}

export interface DataQualityTimeseriesChartV2Props {
	scoreHistory: QualityScoreHistoryPoint[];
	width?: MantineNumberSize;
	height?: MantineNumberSize;
}

export const DataQualityTimeseriesChartV2 = memo(
	({ scoreHistory, width, height }: DataQualityTimeseriesChartV2Props) => {
		const theme = useMantineTheme();
		const containerRef = useRef<HTMLDivElement>(null);
		const [chartWidth, setChartWidth] = useState<number | undefined>();

		/**
		 * Updates the chart width when the container size changes.
		 * Used by the resize observer to maintain responsive behavior.
		 */
		const updateChartWidth = useCallback(() => {
			if (containerRef.current) {
				setChartWidth(containerRef.current.clientWidth);
			}
		}, []);

		// Set up resize observer to maintain responsive chart dimensions
		// eslint-disable-next-line no-use-effect/no-use-effect
		useEffect(() => {
			updateChartWidth();

			const resizeObserver = new ResizeObserver(() => {
				updateChartWidth();
			});

			if (containerRef.current) {
				resizeObserver.observe(containerRef.current);
			}

			return () => {
				resizeObserver.disconnect();
			};
		}, [updateChartWidth]);

		const points = useMemo<ChartPoint[]>(
			() =>
				scoreHistory
					.slice()
					.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
					.map((historyPoint) => {
						const percentage =
							historyPoint.total_possible > 0
								? (historyPoint.total_score / historyPoint.total_possible) * 100
								: 0;
						const policies = Object.values(historyPoint.policy_breakdown ?? {})
							.map((policy) => ({
								id: policy.policy_id,
								name: policy.policy_name,
								percentage: policy.percentage,
							}))
							.sort((a, b) => b.percentage - a.percentage);

						return {
							date: dayjs(historyPoint.date).toDate(),
							isoDate: historyPoint.date,
							percentage,
							policies,
						};
					}),
			[scoreHistory]
		);

		const isCrossingYears = useMemo(
			() => uniq(points.map(({ date }) => dayjs(date).year())).length > 1,
			[points]
		);
		const dateFormat = isCrossingYears ? 'MMM D, YYYY' : 'MMM D';

		const renderTooltip = useCallback(
			({
				x,
				y,
				dataIndex,
			}: {
				x: number;
				y: number;
				dataIndex: number | null;
			}) => {
				if (isNil(dataIndex) || !points[dataIndex]) {
					return null;
				}

				const point = points[dataIndex];

				return (
					<PolicyTimeseriesTooltipV2
						x={x}
						y={y}
						date={point.isoDate}
						overallPercentage={point.percentage}
						policies={point.policies}
					/>
				);
			},
			[points]
		);

		// eslint-disable-next-line no-use-effect/no-use-effect
		useEffect(() => {
			const plot = Plot.plot({
				width: chartWidth,
				height: typeof height === 'number' ? height : undefined,
				marginTop: CHART_CONFIG.MARGIN_TOP,
				marginBottom: CHART_CONFIG.MARGIN_BOTTOM,
				marginLeft: CHART_CONFIG.MARGIN_LEFT,
				marginRight: CHART_CONFIG.MARGIN_RIGHT,
				x: {
					nice: true,
				},
				y: {
					domain: CHART_CONFIG.Y_DOMAIN,
				},
				color: {
					domain: CHART_CONFIG.Y_DOMAIN,
					type: 'linear',
				},
				marks: [
					Plot.gridY({
						ticks: CHART_CONFIG.GRID_TICKS,
						stroke: theme.other.getColor('border/secondary/default'),
						strokeOpacity: 1,
					}),
					Plot.axisX({
						anchor: 'bottom',
						tickFormat: (t: Date) => dayjs.utc(t).format(dateFormat),
						label: null,
						tickSize: 0,
						fontSize: CHART_CONFIG.AXIS_FONT_SIZE,
						color: theme.other.getColor('text/secondary/default'),
						tickPadding: CHART_CONFIG.TICK_PADDING,
						textAnchor: 'middle',
					}),
					Plot.axisY({
						tickFormat: (t: number) => `${t}%`,
						label: null,
						ticks: CHART_CONFIG.GRID_TICKS,
						tickSize: 0,
						fontSize: CHART_CONFIG.AXIS_FONT_SIZE,
						color: theme.other.getColor('text/secondary/default'),
						tickPadding: CHART_CONFIG.TICK_PADDING,
					}),
					Plot.line(points, {
						x: 'date',
						y: 'percentage',
						stroke: 'url(#gradient-v2)',
					}),
					function GradientPlot(_index, { y }) {
						return svg`<defs>
						<linearGradient id="gradient-v2" gradientUnits="userSpaceOnUse"
							x1=0 x2=0 y1=${y?.(0)} y2=${y?.(100)}>
								<stop offset=0% stop-color=${theme.other.getColor('fill/warning/default')} />
								<stop offset=40% stop-color=${theme.other.getColor('fill/warning/default')} />
								<stop offset=41% stop-color=${theme.other.getColor('fill/caution/default')} />
								<stop offset=60% stop-color=${theme.other.getColor('fill/caution/default')} />
								<stop offset=61% stop-color=${theme.other.getColor('fill/info/default')} />
								<stop offset=80% stop-color=${theme.other.getColor('fill/info/default')} />
								<stop offset=81% stop-color=${theme.other.getColor('fill/success/default')} />
								<stop offset=100% stop-color=${theme.other.getColor('fill/success/default')} />`;
					},
					tooltip(
						points,
						pointer({
							x: 'date',
							y: (d: ChartPoint) => d.percentage,
							tooltipRenderer: renderTooltip,
						})
					),
				],
			});
			containerRef.current?.appendChild(plot);
			return () => {
				plot.remove();
				cleanUpTooltips();
			};
		}, [chartWidth, dateFormat, height, renderTooltip, theme, points]);

		return <Box w={width} h={height} ref={containerRef} />;
	}
);
DataQualityTimeseriesChartV2.displayName = 'DataQualityTimeseriesChartV2';
